import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import { FaUserCheck, FaCheck, FaSearch, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { formatRdvDateTime } from '../utils/formatRdvDateTime';
import { getEtatDisplayWithSousEtat, getEtatTableAbbr } from '../utils/etatSignerComplet';
import { abbreviateCentreName } from '../utils/tableAbbreviations';
import { getFicheRowByEtatClassName } from '../utils/etatColorContrast';
import { generateFicheClientPdf } from '../utils/generateFicheClientPdf';
import {
  getFicheTableIndicators,
  ficheHasVisibleDecalageRequest,
  getDecalageDisplayInfo,
} from '../utils/ficheTableIndicators';
import { usesAdminFicheLayout, isHonoreASuivreBadgeSession } from '../utils/adminMenuUrls';
import {
  FicheAdminBadgeHeaders,
  FicheAdminValideCell,
  FicheAdminActionCell,
  FicheAdminIndicatorCells,
} from '../components/fiches/FicheAdminBadgeColumns';
import {
  FicheRowIndexHeader,
  FicheRowIndexCell,
  getFicheRowNumber,
} from '../components/fiches/FicheRowIndexColumn';
import FicheDetailLink from '../components/FicheDetailLink';
import './Affectation.css';
import useForceDesktopViewport from '../hooks/useForceDesktopViewport';

function getProduitName(produitId) {
  return produitId === 1 ? 'PAC' : produitId === 2 ? 'PV' : '';
}

function getProduitColor(produitId) {
  return produitId === 1 ? '#66D5D4' : produitId === 2 ? '#818cf8' : '#cccccc';
}

function getDecalageInfo(fiche) {
  const info = getDecalageDisplayInfo(fiche);
  if (!info || info.status === 'cancelled') return null;
  return info;
}

function formatConfirmateurs(fiche) {
  const parts = [
    fiche.confirmateur_nom,
    fiche.confirmateur_2_pseudo,
    fiche.confirmateur_3_pseudo,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : '';
}

function formatCommercials(fiche) {
  const parts = [fiche.commercial_nom, fiche.commercial_2_pseudo].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : '';
}

const Affectation = () => {
  useForceDesktopViewport('affectation-page');
  const { user } = useAuth();
  const isAdminFicheLayout = usesAdminFicheLayout(user);
  const showHasBadge = isHonoreASuivreBadgeSession(user);
  const queryClient = useQueryClient();
  const [selectedFiches, setSelectedFiches] = useState([]);
  const [selectedCommercial, setSelectedCommercial] = useState('');
  const [filters, setFilters] = useState({
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: new Date().toISOString().split('T')[0],
    id_centre: '',
    produit: '',
    departement: '', // Code département (2 premiers chiffres du code postal)
    id_commercial: '' // Filtre par commercial
  });
  /** Par défaut : date RDV de la première à la dernière heure */
  const [sortConfig, setSortConfig] = useState({
    key: 'date_rdv_time',
    direction: 'asc',
  });

  // Récupérer les données de référence
  const { data: centresData } = useQuery('centres', async () => {
    const res = await api.get('/management/centres');
    return res.data.data || [];
  });

  const { data: commerciauxData } = useQuery('commerciaux', async () => {
    const res = await api.get('/management/utilisateurs');
    return res.data.data?.filter(u => u.fonction === 5) || [];
  });

  const { data: produitsData } = useQuery('produits', async () => {
    const res = await api.get('/management/produits');
    return res.data.data || [];
  });

  const { data: etatsData } = useQuery('etats', async () => {
    const res = await api.get('/management/etats');
    return res.data.data || [];
  });

  // Récupérer les fiches confirmées
  const { data: fichesData, isLoading, refetch } = useQuery(
    ['fiches-confirmees', filters],
    async () => {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'all') delete params[key];
      });
      const res = await api.get('/affectations/fiches-confirmees', { params });
      return res.data.data || [];
    },
    { enabled: true }
  );

  // Mutation pour affecter
  const affectMutation = useMutation(
    async ({ fiches_ids, id_commercial }) => {
      const res = await api.post('/affectations/affecter', {
        fiches_ids,
        id_commercial: parseInt(id_commercial)
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('fiches-confirmees');
        setSelectedFiches([]);
        setSelectedCommercial('');
      }
    }
  );

  // Mutation pour désaffecter
  const desaffectMutation = useMutation(
    async ({ fiches_ids }) => {
      const res = await api.post('/affectations/desaffecter', {
        fiches_ids
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('fiches-confirmees');
        setSelectedFiches([]);
      }
    }
  );

  const handleSelectFiche = (ficheId) => {
    setSelectedFiches(prev => {
      if (prev.includes(ficheId)) {
        return prev.filter(id => id !== ficheId);
      } else {
        return [...prev, ficheId];
      }
    });
  };

  const handleSort = (key) => {
    if (!key) return;
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (!key || sortConfig.key !== key) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon sort-active" />
      : <FaSortDown className="sort-icon sort-active" />;
  };

  const getSortValue = (fiche, key) => {
    if (key === 'commercial') return formatCommercials(fiche).toLowerCase();
    if (key === 'confirmateur') return formatConfirmateurs(fiche).toLowerCase();
    if (key === 'centre') return String(fiche.centre_nom || '').toLowerCase();
    if (key === 'etat') {
      return String(getEtatDisplayWithSousEtat(fiche, etatsData || []) || '').toLowerCase();
    }
    if (key === 'produit') {
      return String(getProduitName(fiche.produit) || fiche.produit_nom || '').toLowerCase();
    }
    if (key === 'valider') return Number(fiche.valider) || 0;
    if (key === 'tel') return String(fiche.tel || fiche.gsm1 || '').toLowerCase();
    if (key === 'cp') return String(fiche.cp || fiche.code_postal || '').toLowerCase();
    if (key === 'nom') return String(fiche.nom || '').toLowerCase();
    if (key === 'prenom') return String(fiche.prenom || '').toLowerCase();
    if (key === 'date_rdv_time' || key === 'date_insert_time') {
      const t = new Date(fiche[key] || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    const value = fiche[key];
    if (value == null) return '';
    return String(value).toLowerCase();
  };

  const sortedFiches = useMemo(() => {
    const list = Array.isArray(fichesData) ? [...fichesData] : [];
    if (!sortConfig.key) return list;
    list.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const cmp = String(aValue ?? '').localeCompare(String(bValue ?? ''), 'fr', {
        numeric: true,
        sensitivity: 'base',
      });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [fichesData, sortConfig, etatsData]);

  const handleSelectAll = () => {
    if (selectedFiches.length === sortedFiches.length && sortedFiches.length > 0) {
      setSelectedFiches([]);
    } else {
      setSelectedFiches(sortedFiches.map((f) => f.id));
    }
  };

  const renderSortableTh = (label, key, className = '') => (
    <th
      className={`sortable-header${className ? ` ${className}` : ''}`}
      onClick={() => handleSort(key)}
      title={`Trier par ${label}`}
    >
      <span className="header-label-wrap">
        {label}
        {renderSortIcon(key)}
      </span>
    </th>
  );

  const handleAffecter = () => {
    if (selectedFiches.length === 0) {
      alert('Veuillez sélectionner au moins une fiche');
      return;
    }
    if (!selectedCommercial || selectedCommercial <= 0) {
      alert('Veuillez sélectionner un commercial');
      return;
    }
    if (window.confirm(`Affecter ${selectedFiches.length} fiche(s) au commercial sélectionné ?`)) {
      affectMutation.mutate({
        fiches_ids: selectedFiches,
        id_commercial: selectedCommercial
      });
    }
  };

  const handleDesaffecter = () => {
    if (selectedFiches.length === 0) {
      alert('Veuillez sélectionner au moins une fiche');
      return;
    }
    if (window.confirm(`Désaffecter ${selectedFiches.length} fiche(s) ?`)) {
      desaffectMutation.mutate({
        fiches_ids: selectedFiches
      });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEtatColor = (fiche) => {
    if (fiche?.etat_color) return fiche.etat_color;
    const etat = (etatsData || []).find((e) => e.id === fiche?.id_etat_final || e.id === Number(fiche?.id_etat_final));
    return etat?.color || '#cccccc';
  };

  const checkIndicators = (histoString, fiche = {}) =>
    getFicheTableIndicators(histoString, fiche, etatsData || []);

  const handleGeneratePdf = async (hash) => {
    if (!hash) return;
    try {
      const [ficheRes, profRes, tcRes] = await Promise.all([
        api.get(`/fiches/${encodeURIComponent(hash)}`),
        api.get('/management/professions'),
        api.get('/management/type-contrat'),
      ]);
      const full = ficheRes.data?.data;
      if (!full) throw new Error('Fiche introuvable');
      generateFicheClientPdf(full, {
        professions: profRes.data?.data || [],
        typeContrat: tcRes.data?.data || [],
        centres: centresData || [],
        commerciaux: commerciauxData || [],
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Impossible de générer le PDF');
    }
  };

  const showDecalageColAdmin =
    isAdminFicheLayout && (fichesData || []).some((f) => ficheHasVisibleDecalageRequest(f));

  return (

    <div className="affectation-page">
      <h2 className="page-title"><FaUserCheck /> Affectation des Fiches Confirmées</h2>

      {/* Filtres */}
      <div className="affectation-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Date RDV début</label>
            <input
              type="date"
              value={filters.date_debut}
              onChange={(e) => handleFilterChange('date_debut', e.target.value)}
              className="form-control"
              title="Filtrer par date de rendez-vous - Date de début"
            />
          </div>

          <div className="filter-group">
            <label>Date RDV fin</label>
            <input
              type="date"
              value={filters.date_fin}
              onChange={(e) => handleFilterChange('date_fin', e.target.value)}
              className="form-control"
              title="Filtrer par date de rendez-vous - Date de fin"
            />
          </div>

          <div className="filter-group">
            <label>Centre</label>
            <select
              value={filters.id_centre}
              onChange={(e) => handleFilterChange('id_centre', e.target.value)}
              className="form-control"
            >
              <option value="">Tous les centres</option>
              {centresData?.map(centre => (
                <option key={centre.id} value={centre.id}>{centre.titre}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Produit</label>
            <select
              value={filters.produit}
              onChange={(e) => handleFilterChange('produit', e.target.value)}
              className="form-control"
            >
              <option value="">Tous les produits</option>
              {produitsData?.map(prod => (
                <option key={prod.id} value={prod.id}>{prod.titre}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Département</label>
            <input
              type="text"
              value={filters.departement}
              onChange={(e) => handleFilterChange('departement', e.target.value)}
              className="form-control"
              placeholder="Ex: 01, 75, 13"
              title="Filtrer par un ou plusieurs codes département, séparés par une virgule"
            />
          </div>

          <div className="filter-group">
            <label>Commercial</label>
            <select
              value={filters.id_commercial}
              onChange={(e) => handleFilterChange('id_commercial', e.target.value)}
              className="form-control"
            >
              <option value="">Tous les commerciaux</option>
              {commerciauxData?.map(com => (
                <option key={com.id} value={com.id}>{com.pseudo}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Actions d'affectation */}
      <div className="affectation-actions">
        <div className="affectation-assign-row">
          <div className="action-group">
            <label>Commercial à affecter</label>
            <select
              value={selectedCommercial}
              onChange={(e) => setSelectedCommercial(e.target.value)}
              className="form-control commercial-select"
            >
              <option value="">Sélectionner un commercial</option>
              {commerciauxData?.map(com => (
                <option key={com.id} value={com.id}>{com.pseudo}</option>
              ))}
            </select>
          </div>

          <div className="action-buttons">
            <button
              onClick={handleAffecter}
              disabled={selectedFiches.length === 0 || !selectedCommercial || affectMutation.isLoading}
              className="btn btn-affect"
            >
              {affectMutation.isLoading ? 'Affectation...' : 'Affecter'}
            </button>
            <button
              onClick={handleDesaffecter}
              disabled={selectedFiches.length === 0 || desaffectMutation.isLoading}
              className="btn btn-desaffect"
            >
              {desaffectMutation.isLoading ? 'Désaffectation...' : 'Désaffecter'}
            </button>
          </div>
        </div>
      </div>

      {/* Messages de succès/erreur */}
      {affectMutation.isSuccess && (
        <div className="alert alert-success">
          {affectMutation.data.message}
        </div>
      )}
      {affectMutation.isError && (
        <div className="alert alert-error">
          Erreur lors de l'affectation: {affectMutation.error?.response?.data?.message || affectMutation.error.message}
        </div>
      )}
      {desaffectMutation.isSuccess && (
        <div className="alert alert-success">
          {desaffectMutation.data.message}
        </div>
      )}
      {desaffectMutation.isError && (
        <div className="alert alert-error">
          Erreur lors de la désaffectation: {desaffectMutation.error?.response?.data?.message || desaffectMutation.error.message}
        </div>
      )}

      {/* Liste des fiches */}
      <div className="fiches-list">
        <div className="list-header">
          <div className="select-all">
            <input
              type="checkbox"
              checked={selectedFiches.length === sortedFiches.length && sortedFiches.length > 0}
              onChange={handleSelectAll}
            />
            <span>Tout sélectionner ({sortedFiches.length} fiches)</span>
          </div>
          <div className="selected-count">
            {selectedFiches.length} fiche(s) sélectionnée(s)
          </div>
        </div>

        {isLoading ? (
          <div className="loading">Chargement des fiches...</div>
        ) : sortedFiches.length === 0 ? (
          <div className="no-data">
            Aucune fiche confirmée trouvée
          </div>
        ) : (
          <div className="fiches-table-container">
<table className={`fiches-table${isAdminFicheLayout ? ' fiches-table--admin-layout' : ''}`}>
              <thead>
                <tr>
                  <th className="affectation-select-col"></th>
                  <FicheRowIndexHeader />
                  {renderSortableTh('Nom', 'nom', 'fiche-col-nom')}
                  {renderSortableTh('Prénom', 'prenom', 'fiche-col-prenom')}
                  {renderSortableTh('Téléphone', 'tel')}
                  {renderSortableTh('CP', 'cp')}
                  {isAdminFicheLayout ? (
                    <>
                      {renderSortableTh('Date RDV', 'date_rdv_time', 'fiche-col-date')}
                      {renderSortableTh('Commercial', 'commercial')}
                      <FicheAdminBadgeHeaders
                        onSortValide={() => handleSort('valider')}
                        onSortProduit={() => handleSort('produit')}
                        getSortIcon={(label) =>
                          renderSortIcon(
                            label === 'Validé' ? 'valider' : label === 'Produit' ? 'produit' : label
                          )
                        }
                      />
                      {renderSortableTh('Centre', 'centre')}
                      {renderSortableTh('Date Insertion', 'date_insert_time', 'fiche-col-date')}
                      {renderSortableTh('Conf', 'confirmateur')}
                      {renderSortableTh('État Final', 'etat')}
                      {showDecalageColAdmin ? <th className="dashboard-decalage-col">Décalage</th> : null}
                    </>
                  ) : (
                    <>
                      {renderSortableTh('Date Insertion', 'date_insert_time', 'fiche-col-date')}
                      {renderSortableTh('Date RDV', 'date_rdv_time', 'fiche-col-date')}
                      {renderSortableTh('État Final', 'etat')}
                      {renderSortableTh('Confirmateur', 'confirmateur')}
                      {renderSortableTh('Commercial', 'commercial')}
                      {renderSortableTh('Centre', 'centre')}
                      {renderSortableTh('Produit', 'produit')}
                      {renderSortableTh('Validé', 'valider')}
                      <th className="affectation-actions-col">Actions</th>
                      <th className="dashboard-decalage-col">État décalage</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedFiches.map((fiche, rowIndex) => {
                  const indicators = checkIndicators(fiche.id_etat_histo, fiche);
                  const etatColor = getEtatColor(fiche);
                  const produitColor = getProduitColor(fiche.produit);
                  const decalageInfo = isAdminFicheLayout
                    ? (showDecalageColAdmin ? getDecalageDisplayInfo(fiche) : null)
                    : getDecalageInfo(fiche);
                  return (
                    <tr
                      key={fiche.id}
                      className={getFicheRowByEtatClassName(etatColor, selectedFiches.includes(fiche.id) ? 'selected' : '')}
                      style={{
                        backgroundColor: etatColor,
                      }}
                    >
                      <td className="affectation-select-col">
                        <input
                          type="checkbox"
                          checked={selectedFiches.includes(fiche.id)}
                          onChange={() => handleSelectFiche(fiche.id)}
                        />
                      </td>
                      <FicheRowIndexCell number={getFicheRowNumber(rowIndex)} />
                      <td data-label="Nom:" className="fiche-row-lead-cell fiche-col-nom">
                        <span className="fiche-row-lead-label">{fiche.nom || ''}</span>
                      </td>
                      <td data-label="Prénom:" className="fiche-col-prenom">{fiche.prenom || ''}</td>
                      <td data-label="Téléphone:">{fiche.tel || fiche.gsm1 || ''}</td>
                      <td data-label="CP:">{fiche.cp || fiche.code_postal || ''}</td>
                      {isAdminFicheLayout ? (
                        <>
                          <td data-label="Date RDV:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatRdvDateTime(fiche.date_rdv_time)}</td>
                          <td data-label="Commercial:">{formatCommercials(fiche)}</td>
                          <FicheAdminValideCell valider={fiche.valider} confRdvAvec={fiche.conf_rdv_avec} />
                          <FicheAdminActionCell
                            DetailButtonTag={FicheDetailLink}
                            detailButtonProps={{ ficheHash: fiche.hash, ficheId: fiche.id }}
                          />
                          <FicheAdminIndicatorCells
                            indicators={indicators}
                            produitName={getProduitName(fiche.produit) || fiche.produit_nom}
                            produitColor={produitColor}
                            onPdf={() => handleGeneratePdf(fiche.hash)}
                          />
                          <td data-label="Centre:" className="fiche-col-centre" title={fiche.centre_nom || undefined}>{abbreviateCentreName(fiche.centre_nom || '')}</td>
                          <td data-label="Date Insertion:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatDate(fiche.date_insert_time)}</td>
                          <td data-label="Confirmateur:">{formatConfirmateurs(fiche)}</td>
                          <td data-label="État:" className="etat-col-cell">
                            <span className="etat-text" title={getEtatDisplayWithSousEtat(fiche, etatsData || []) || undefined}>
                              {getEtatTableAbbr(fiche, etatsData || [])}
                            </span>
                          </td>
                          {showDecalageColAdmin ? (
                            <td data-label="Décalage:" className="dashboard-decalage-cell">
                              {decalageInfo ? (
                                <span className={`dashboard-decalage-mention is-${decalageInfo.status}`}>
                                  {decalageInfo.text}
                                </span>
                              ) : null}
                            </td>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <td data-label="Date Insertion:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatDate(fiche.date_insert_time)}</td>
                          <td data-label="Date RDV:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatRdvDateTime(fiche.date_rdv_time)}</td>
                          <td data-label="État:" className="etat-col-cell">
                            <span className="etat-text" title={getEtatDisplayWithSousEtat(fiche, etatsData || []) || undefined}>
                              {getEtatTableAbbr(fiche, etatsData || [])}
                            </span>
                          </td>
                          <td data-label="Confirmateur:">{formatConfirmateurs(fiche)}</td>
                          <td data-label="Commercial:">{formatCommercials(fiche)}</td>
                          <td data-label="Centre:" className="fiche-col-centre" title={fiche.centre_nom || undefined}>{abbreviateCentreName(fiche.centre_nom || '')}</td>
                          <td data-label="Produit:">
                            <span
                              className="produit-indicator"
                              style={{ backgroundColor: produitColor, color: '#ffffff' }}
                              title={getProduitName(fiche.produit) || fiche.produit_nom}
                            >
                              {getProduitName(fiche.produit) || fiche.produit_nom || ''}
                            </span>
                          </td>
                          <td data-label="Validé:" style={{ textAlign: 'center' }}>
                            {fiche.valider > 0 ? (
                              <FaCheck
                                style={{ color: '#000000', fontSize: '15.3px' }}
                                title={`Validée${fiche.conf_rdv_avec ? ` avec ${fiche.conf_rdv_avec}` : ''}`}
                              />
                            ) : null}
                          </td>
                          <td data-label="" className="affectation-actions-col">
                            <div className="fiche-indicators">
                              {indicators.r2 && (
                                <span className="indicator r2" title="R2 placé (commercial secondaire)">R2</span>
                              )}
                              {showHasBadge && indicators.has && (
                                <span className="indicator has" title="Honoré à suivre">HAS</span>
                              )}
                              {indicators.rf && <span className="indicator rf" title="Refus">REF</span>}
                              {indicators.sg && <span className="indicator sg" title="Signé">SIG</span>}
                              {indicators.an && <span className="indicator an" title="Annulation">ANN</span>}
                              {indicators.cs && <span className="indicator rs" title="SEUL">SEUL</span>}
                            </div>
                            <div className="dashboard-actions-cell">
                              <FicheDetailLink
                                ficheHash={fiche.hash}
                                ficheId={fiche.id}
                                className="btn-detail"
                                title="Voir les détails"
                              >
                                <FaSearch style={{ color: '#ffffff', fontSize: '11.9px' }} />
                              </FicheDetailLink>
                            </div>
                          </td>
                          <td data-label="État décalage:" className="dashboard-decalage-cell">
                            {decalageInfo && (
                              <span className={`dashboard-decalage-mention is-${decalageInfo.status}`}>
                                {decalageInfo.text}
                              </span>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Affectation;

